<template>
  <form class="flex flex-col gap-y-4 font-Spectral">
    <div class="flex justify-between gap-[2%]">
      <FloatingLabelTextInput
        aria-label="firstName"
        autocomplete="given-name"
        label="First name"
        :error="errorMessages.firstName"
        :model-value="form.step2.firstName"
        :on-update-model-value="updateField.firstName"
      />
      <FloatingLabelTextInput
        aria-label="lastName"
        autocomplete="family-name"
        label="Last name"
        :error="errorMessages.lastName"
        :model-value="form.step2.lastName"
        :on-update-model-value="updateField.lastName"
      />
    </div>
    <FloatingLabelTextInput
      aria-label="email"
      autocomplete="email"
      label="Email"
      type="email"
      :error="errorMessages.email"
      :model-value="form.step2.email"
      :on-update-model-value="updateField.email"
    />
    <div>
      <!-- 代理予約の参加者は S3 で聞く。ここに残すのは予約者側の会社名だけ（D-042） -->
      <button aria-controls="company-fields" class="flex w-full items-center gap-x-2 py-1 text-left text-sm text-gold1" type="button" :aria-expanded="isCompanyFormOpen" @click="onToggleCompanyForm">
        Booking for a company?<span aria-hidden="true">{{ disclosureMark }}</span>
      </button>
      <div v-show="isCompanyFormOpen" id="company-fields" class="mt-4">
        <FloatingLabelTextInput
          aria-label="companyName"
          autocomplete="organization"
          label="Company name"
          :model-value="form.step2.companyName"
          :on-update-model-value="updateField.companyName"
          :required="false"
        />
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import FloatingLabelTextInput from '@/wabunka-src/common/components/stateful/FloatingLabelTextInput/FloatingLabelTextInput.vue';
import { useGlobalWabunkaReservationForm } from '@/wabunka-src/common/features/Reservation/composables/forms/useGlobalWabunkaReservationForm';
import { resolveSomeoneElseFormOnCompanyNameChange } from '@/wabunka-src/common/features/Reservation/core/requiredFields/someoneElseForm';

interface Props {
  getErrorMessage: (s: string) => string;
}

const props = defineProps<Props>();

const { form } = useGlobalWabunkaReservationForm();

const errorMessages = computed(() => ({
  firstName: props.getErrorMessage('step2.firstName'),
  lastName: props.getErrorMessage('step2.lastName'),
  email: props.getErrorMessage('step2.email'),
}));

const isCompanyFieldExpanded = ref(false);

const setCompanyName = (value: string) => {
  form.value.step2.shouldShowSomeoneElseForm = resolveSomeoneElseFormOnCompanyNameChange(form.value.step2, value);
  form.value.step2.companyName = value;
};

const updateField = {
  firstName: (value: string) => (form.value.step2.firstName = value),
  lastName: (value: string) => (form.value.step2.lastName = value),
  email: (value: string) => (form.value.step2.email = value),
  // 会社名つきで復元された直後は開いていることを会社名だけが示すため、入力中に全部消しても畳まないよう開いた扱いにする
  companyName: (value: string) => {
    setCompanyName(value);
    isCompanyFieldExpanded.value = true;
  },
};

// 会社名が入っていれば開く。3DS 失敗から会社名つきで戻ったとき、閉じたままだと見えないまま予約に載る
const isCompanyFormOpen = computed(() => isCompanyFieldExpanded.value || form.value.step2.companyName !== '');

const disclosureMark = computed(() => (isCompanyFormOpen.value ? '−' : '＋'));

// 会社名は開閉に関わらず送られるため、閉じるときに消す
const onToggleCompanyForm = () => {
  const willOpen = !isCompanyFormOpen.value;
  if (!willOpen) setCompanyName('');
  isCompanyFieldExpanded.value = willOpen;
};
</script>
